// src/components/ReportPage.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {BASE_URL} from "../Apis/apiConfig";



const ReportPage = () => {
  const { startDate: pStart, endDate: pEnd, subject: pSubject, memberName: pMember } = useParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [adminReply, setAdminReply] = useState([]);
  const token = localStorage.getItem("token");

  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [errorAdmin, setErrorAdmin] = useState("");

  const startDate = pStart ? decodeURIComponent(pStart) : "";
  const endDate = pEnd ? decodeURIComponent(pEnd) : "";
  const subject = pSubject && pSubject !== "all" ? decodeURIComponent(pSubject) : "";
  const memberName = pMember && pMember !== "all" ? decodeURIComponent(pMember) : "";

  useEffect(() => {
    if (!startDate || !endDate) {
      setError("Missing date range. Please go back and select From and To date.");
      setLoading(false);
      return;
    }

    const fetchReport = async () => {
      setLoading(true);
      setError("");
      try {
        const resp = await axios.get(`${BASE_URL}/reports/get-report`, {
          params: {
            startDate,
            endDate,
            subject,
            memberName,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = resp.data;
        if (Array.isArray(data)) setRows(data);
        else if (data == null) setRows([]);
        else setRows([data]);
      } catch (err) {
        console.error("fetch report error:", err);
        setError("Failed to fetch report. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [startDate, endDate, subject, memberName, token]);

  useEffect(() => {
    const fetchAdminChat = async () => {
      setLoadingAdmin(true);
      setErrorAdmin("");
      try {
        const resp = await axios.get(`${BASE_URL}/reports/get-admin-answer`, {
          params: {
            memberName,
            subject,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = resp.data;
        if (Array.isArray(data)) setAdminReply(data);
        else if (data == null) setAdminReply([]);
        else setAdminReply([data]);
      } catch (err) {
        console.error("fetch admin chat error:", err);
        setErrorAdmin("Failed to fetch chat. Please try again.");
      } finally {
        setLoadingAdmin(false);
      }
    };

    if (token) {
      fetchAdminChat();
    }
  }, [token, memberName, subject]);


  const isSendPdfTrue = (val) => {
    if (val === true) return true;
    if (typeof val === "string") {
      const lower = val.toLowerCase();
      return lower === "true" || lower === "1";
    }
    if (typeof val === "number") return val === 1;
    return false;
  };


  const norm = (v) => (v === null || v === undefined ? null : String(v).trim());


  const parseTime = (d) => {
    if (!d) return null;
    const t = Date.parse(String(d));
    if (isNaN(t)) return null;
    return t;
  };

  const TIME_TOLERANCE = 2 * 60 * 1000; 

  const findReplyForRow = (row, rowIndex) => {
    if (!adminReply || adminReply.length === 0) return null;

    const rowIds = [
      norm(row.admidChatId),
      norm(row.admidchatid),
      norm(row.chat_id),
      norm(row.chatId),
      norm(row.id),
    ].filter(Boolean);

    if (rowIds.length > 0) {
      const matchById = adminReply.find((a) => {
        const aIds = [
          norm(a.admidChatId),
          norm(a.admidchatid),
          norm(a.chat_id),
          norm(a.chatId),
          norm(a.id),
        ].filter(Boolean);
        return aIds.some((ak) => rowIds.includes(ak));
      });
      if (matchById) return matchById;
    }

  
    const rowFile = norm(row.pdfFileName) || norm(row.pdffile) || norm(row.pdf);
    if (rowFile) {
      const matchByFile = adminReply.find((a) => {
        const af = norm(a.pdfFileName) || norm(a.pdffile) || norm(a.pdf);
        return af && af === rowFile;
      });
      if (matchByFile) return matchByFile;
    }


    const rowTime = parseTime(row.date ?? row.Date ?? row.createdAt ?? row.created_at);
    if (rowTime !== null) {
      const matchByTime = adminReply.find((a) => {
        const aTime = parseTime(a.date ?? a.Date ?? a.createdAt ?? a.created_at);
        if (aTime === null) return false;
        return Math.abs(aTime - rowTime) <= TIME_TOLERANCE;
      });
      if (matchByTime) return matchByTime;
    }

    const byIndex = adminReply[rowIndex] ?? null;
    return byIndex;
  };

  const base64ToBlob = (base64, mime = "application/pdf") => {
    try {
      const binary = atob(base64);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new Blob([bytes.buffer], { type: mime });
    } catch (e) {
      console.error("base64ToBlob error:", e);
      return null;
    }
  };

  const downloadRowPdf = (base64, filename = "document.pdf") => {
    if (!base64) {
      alert("No PDF available to download for this row.");
      return;
    }
    const blob = base64ToBlob(base64, "application/pdf");
    if (!blob) {
      alert("Invalid PDF data.");
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const printRowPdf = (base64) => {
    if (!base64) {
      alert("No PDF available to print for this row.");
      return;
    }
    const w = window.open("");
    if (!w) {
      alert("Popup blocked. Please allow popups to print.");
      return;
    }
    const html = `
      <html>
        <head><title>Print PDF</title></head>
        <body style="margin:0">
          <embed width="100%" height="100%" src="data:application/pdf;base64,${base64}" type="application/pdf"></embed>
          <script>
            setTimeout(() => { try { window.print(); } catch(e){console.error(e)} }, 500);
          </script>
        </body>
      </html>
    `;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) return resolve();
      const s = document.createElement("script");
      s.src = src;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("Failed to load " + src));
      document.head.appendChild(s);
    });

  const downloadPageAsPdf = async () => {
    try {
      if (!window.html2canvas) {
        await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
      }
      if (!window.jspdf) {
        await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
      }

      const html2canvas = window.html2canvas;
      const { jsPDF } = window.jspdf || window.jspdf || {};

      if (!html2canvas || !jsPDF) throw new Error("PDF libs not loaded");

      const element = document.body; 
      const originalBg = element.style.backgroundColor;
      element.style.backgroundColor = "#ffffff";

      const canvas = await html2canvas(element, { useCORS: true, scale: 1.5 });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = imgWidth / pdfWidth;
      const scaledHeight = imgHeight / ratio;

      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, scaledHeight);

      let remaining = scaledHeight - pdfHeight;
      while (remaining > -1) {
        position -= pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, scaledHeight);
        remaining -= pdfHeight;
      }

      element.style.backgroundColor = originalBg;
      pdf.save(`report_page_${new Date().toISOString().slice(0, 19).replaceAll(":", "-")}.pdf`);
    } catch (err) {
      console.error("downloadPageAsPdf error:", err);
      if (window.confirm("Automatic PDF generation failed. Open print dialog instead (you can Save as PDF)?")) {
        window.print();
      }
    }
  };

  const printPage = () => window.print();
  const renderAdminBadge = (row, idx) => {
    const match = findReplyForRow(row, idx);
    if (match && isSendPdfTrue(match.sendPdf)) {
      return <span style={approvedStyle}>Solved</span>;
    }
    return <span style={pendingStyle}>Pending</span>;
  };
  const renderStatusText = (row, idx) => {
    const match = findReplyForRow(row, idx);
    if (match && isSendPdfTrue(match.sendPdf)) return "ok";
    return "Unchecked";
  };


  const formatDate = (d) => {
    if (!d) return null;
    const t = Date.parse(String(d));
    if (isNaN(t)) return null;
    const dt = new Date(t);
    const day = String(dt.getDate()).padStart(2, "0");
    const month = String(dt.getMonth() + 1).padStart(2, "0");
    const year = dt.getFullYear();
    return `${day}/${month}/${year}`;
  };


  const formatTime = (d) => {
    if (!d) return null;
    const t = Date.parse(String(d));
    if (isNaN(t)) return null;
    const dt = new Date(t);
    let hrs = dt.getHours();
    const mins = String(dt.getMinutes()).padStart(2, "0");
    const ampm = hrs >= 12 ? "pm" : "am";
    hrs = hrs % 12;
    hrs = hrs === 0 ? 12 : hrs;
    return `${hrs}:${mins}${ampm}`;
  };


  const formattedDateTime = (d1, d2) => {
    const d = d1 ?? d2;
    if (!d) return "-";
    const datePart = formatDate(d);
    const timePart = formatTime(d);
    if (!datePart && !timePart) return "-";
    if (!timePart) return datePart;
    return `${datePart} ${timePart}`;
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>Report</h2>

      <div style={{ marginBottom: "12px" }}>
        <strong>From:</strong> {startDate} &nbsp; <strong>To:</strong> {endDate}
        {subject && (
          <>
            &nbsp; | <strong>Subject:</strong> {subject}
          </>
        )}
        {memberName && (
          <>
            &nbsp; | <strong>Flat:</strong> {memberName}
          </>
        )}
      </div>

      <div style={{ marginBottom: "12px", display: "flex", gap: 8 }}>
        <button onClick={() => navigate(-1)} className="submit-btn">
          Back
        </button>

        <button onClick={printPage} className="submit-btn" title="Print this page">
          Print Page
        </button>
        <button onClick={downloadPageAsPdf} className="submit-btn" title="Download this page as PDF">
          Download Page as PDF
        </button>
      </div>

      {loading ? (
        <div>Loading report...</div>
      ) : error ? (
        <div style={{ color: "red" }}>{error}</div>
      ) : rows.length === 0 ? (
        <div>No records found for the selected filters.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="simple-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Client</th>
                <th style={thStyle}>Admin</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={tdStyle}>
                    {formattedDateTime(r.date ?? r.Date ?? null, r.date ?? r.Date ?? null)}
                  </td>
                  <td style={tdStyle}>{r.title ?? r.Title ?? r.member_name ?? r.memberName ?? "-"}</td>

           
                  <td style={tdStyle}>{renderAdminBadge(r, i)}</td>

                  
                  <td style={tdStyle}>
                    {renderStatusText(r, i)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  border: "1px solid #ddd",
  padding: "8px",
  textAlign: "left",
  background: "#f2f2f2",
};

const tdStyle = {
  border: "1px solid #ddd",
  padding: "8px",
};

const approvedStyle = {
  display: "inline-block",
  padding: "4px 8px",
  borderRadius: 6,
  backgroundColor: "#d4edda",
  color: "#155724",
  fontWeight: 600,
};

const pendingStyle = {
  display: "inline-block",
  padding: "4px 8px",
  borderRadius: 6,
  backgroundColor: "#fff3cd",
  color: "#856404",
  fontWeight: 600,
};

export default ReportPage;
